/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, ScrollView, BackHandler, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useDeviceInfo } from '@airasia-rn/ui';
import { useTranslation } from 'react-i18next';
import { HeaderView } from '../../../../hotels/src/widgets/HeaderWidget';
import { useSnapNavigation } from '../../context/SnapNavigationContext';
import SnackbarView, {
  SnackbarDuration,
} from '@airasia-phoenix/react-native/components/SnackbarView';
import { pd, getColor } from '@airasia-phoenix/react-native/utils/utils';
import { SnapBannerView } from '../SnapBannerView';
import { SearchFormView } from '../SearchFormView';
import { RecentSearchesCarouselView } from '../RecentSearchesCarousel';
import { safeAreaEdges } from '../../utils/safeArea.utils';
import { useHandleSearch, useRecentSearchNavigation } from '../../hooks';
import { useSnapSearchViewFeatureFlag } from '../../hooks/useSnapSearchViewFeatureFlag';
import type { backHandlerProps } from '@airasia-rn/ui/context';
import type { SearchFormState } from '../../types';
import type { SnapNativeListingProps } from '../../utils';
import { useOptionalSnapConfig } from '../../configs/SnapConfigProvider';
import { useLogger } from '@airasia-rn/core-logger';
import { SnapSearchWidgetEvent } from '../../analytics';
import { EVENT_NAMES } from '../../analytics/snapSearchWidget/snapSearchWidget.meta';
import { SNAP_HOTELS_LIST_PATH } from '../../configs/constants';
import ArrowBackIos from '@move-travel/react-native-phoenix-icons/ArrowBackIos';

interface SnapSearchViewProps {
  backHandler?: (params?: backHandlerProps) => void;
}

const SnapSearchView = ({ backHandler }: SnapSearchViewProps) => {
  // Optional on purpose: SNAP's tree can end up mounted without its own
  // provider (see `handleBack`), and throwing at render kills the whole app.
  const snapConfig = useOptionalSnapConfig()?.config;
  const { handleSearch } = useHandleSearch();
  const { isWeb, isMobileWidth } = useDeviceInfo();
  const { navigateBack, navigateToHotelsList } = useSnapNavigation();
  const [toastMessage, setToast] = useState('');
  const { t: tSnap } = useTranslation('snap');
  const { logEvent, logError } = useLogger();
  const showMobileExperience = !isWeb || isMobileWidth;

  const hasTrackedWidgetViewRef = useRef(false);

  // Track SNAP search widget viewed event (aa_snp_searchwidget_viewed)
  useEffect(() => {
    if (hasTrackedWidgetViewRef.current) return;
    hasTrackedWidgetViewRef.current = true;
    const params = SnapSearchWidgetEvent.snapSearchWidgetViewEvent();
    logEvent(EVENT_NAMES.SEARCH_WIDGET_VIEWED, params);
  }, [logEvent]);
  const {
    snap_recent_searches_enabled,
    snap_recent_search_max_count,
    snap_search_banner,
  } = useSnapSearchViewFeatureFlag();

  /**
   * Returns whether the back press was handled, so the Android listener below
   * knows when to let the OS take over instead of swallowing the press.
   *
   * Expo Router keeps its store in a module-level singleton shared by every RN
   * root the super app mounts, and never restores it when a root unmounts.
   * Once another root owns it, `router` targets an unmounted navigation ref:
   * `back()` throws and `canGoBack()` is false, so every router-based path is
   * dead. Each rung below is tried in order of how much context it still has.
   */
  const handleBack = useCallback((): boolean => {
    if (backHandler) {
      backHandler();
      return true;
    }
    if (snapConfig?.backHandler) {
      snapConfig.backHandler();
      return true;
    }

    try {
      const handled = navigateBack() as unknown as boolean | undefined;
      if (handled === false) {
        return false;
      }
      return true;
    } catch (error) {
      logError(error, { context: 'SnapSearchView: navigateBack failed' });
      return false;
    }
  }, [backHandler, snapConfig, navigateBack, logError]);

  // Keep the header button and Android hardware back on the same path. Only
  // consume the press when we handled it - otherwise let it fall through so
  // the host can pop the native screen.
  useEffect(() => {
    if (Platform.OS !== 'android') return;

    const subscription = BackHandler.addEventListener('hardwareBackPress', () =>
      handleBack(),
    );

    return () => subscription.remove();
  }, [handleBack]);

  const handleShowToast = (message: string) => {
    setToast(message);
  };

  const handleDismissToast = () => {
    setToast('');
  };

  const onSearchSubmit = useCallback(
    async (state: SearchFormState) => {
      await handleSearch(state);
    },
    [handleSearch],
  );

  const navigateToUrl = useCallback(
    (url: string) => {
      if (isWeb && typeof window !== 'undefined') {
        const parsedUrl = new URL(
          url,
          window.location?.origin || 'https://www.airasia.com',
        );
        const isSnapListUrl =
          parsedUrl.pathname.endsWith(SNAP_HOTELS_LIST_PATH) ||
          parsedUrl.pathname.endsWith('/snap/hotels/list');

        if (isSnapListUrl) {
          navigateToHotelsList(url);
          return;
        }

        window.open(url, '_self');
        return;
      }

      navigateToHotelsList(url);
    },
    [isWeb, navigateToHotelsList],
  );

  // Use the new hook for recent search navigation
  const { handleRecentSearchCardPress } = useRecentSearchNavigation({
    navigateToUrl,
  });

  return (
    <SafeAreaView style={styles.safeAreaViewContainer} edges={safeAreaEdges}>
      {showMobileExperience ? (
        <HeaderView
          titleStart={tSnap('searchSnapPage.headerTitle')}
          onHeaderBack={handleBack}
          startIcon={<ArrowBackIos />}
          supportingText={''}
        />
      ) : null}
      <View style={styles.mainContainer}>
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
        >
          {showMobileExperience ? (
            <View style={styles.bannerSection}>
              {snap_search_banner === true ? <SnapBannerView /> : null}
            </View>
          ) : null}
          <View style={styles.pageContentContainer}>
            <View style={styles.widgetContainer}>
              <View style={styles.formContainer}>
                <SearchFormView
                  onError={handleShowToast}
                  onSubmit={onSearchSubmit}
                />
              </View>
              {/* Recent Searches Carousel (Scenario 1: hidden for new users, Scenario 2: shown after first search) */}
              {snap_recent_searches_enabled ? (
                <View style={styles.recentSearchesContainer}>
                  <RecentSearchesCarouselView
                    onCardPress={handleRecentSearchCardPress}
                    maxCount={snap_recent_search_max_count}
                  />
                </View>
              ) : null}
            </View>
          </View>
        </ScrollView>
        {toastMessage ? (
          <SnackbarView
            testID="voucher-snackbar"
            message={toastMessage}
            duration={SnackbarDuration.SHORT}
            onDismiss={handleDismissToast}
          />
        ) : null}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create((pd) => ({
  banner: {
    height: 160,
    width: '100%',
    backgroundColor: getColor(pd.sys.colour.bg_secondary),
  },
  safeAreaViewContainer: {
    flex: 1,
    backgroundColor: getColor(pd.sys.colour.container_none),
  },
  mainContainer: {
    flex: 1,
  },
  scrollContainer: {},
  pageContentContainer: {
    width: '100%',
    zIndex: 1,
  },
  bannerSection: {
    position: 'relative',
    width: '100%',
    display: { xs: 'flex', md: 'none' },
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    display: { xs: 'flex', md: 'none' },
  },
  formContainer: {
    width: '100%',
    zIndex: 1,
  },
  widgetContainer: {
    paddingHorizontal: {
      xs: 0,
      md: pd.ref.space.spacing50.px,
    },
    marginHorizontal: {
      xs: 0,
      md: 16,
    },
    width: {
      xs: 'auto',
      md: 'auto',
    },
  },
  recentSearchesContainer: {
    marginTop: pd.ref.space.spacing10.px,
    paddingBottom: pd.ref.space.spacing40.px,
  },
}));

export default SnapSearchView;
